import React, { useEffect, useCallback, useState } from "react";
import {
  Grid,
  Typography,
  Chip,
  Divider,
  OutlinedInput,
  AppBar,
  Button,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  Avatar,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useDispatch, useSelector } from "react-redux";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

import {
  getChangeRequests,
  resetChangeRequests,
  filterChangeRequest,
  deleteChangeRequest,
  acceptChangeRequest,
} from "./state/actions";
import Header from "../../../Layouts/Components/Header";
import useSnackBar from "../../Hooks/useSnackBar";
import ScreenLoader from "../../shared/ScreenLoader";
import { calculateLeftDays } from "../../../utils/helperFunctions";
import getModuleIcon from "../../../utils/getModuleIcon";

const requestOptions = [
  { id: "1", label: "All", value: "ALL" },
  { id: "2", label: "Pending", value: "NEW" },
  { id: "3", label: "Done", value: "OTHER" },
];
const licenseOptions = [
  { id: "1", label: "All", value: "ALL" },
  { id: "2", label: "New", value: "NEW" },
  { id: "3", label: "Active", value: "RUNNING" },
  { id: "4", label: "Expired", value: "EXPIRED" },
];

const ChangeRequests = () => {
  const dispatch = useDispatch();
  const { error, snackbar, success } = useSnackBar();
  const [requestStatus, setRequestStatus] = useState(requestOptions[0].value);
  const [licenseStatus, setLicenseStatus] = useState(licenseOptions[0].value);
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState(null);
  const loadRequests = useCallback(
    () => dispatch(getChangeRequests(error)),
    [dispatch, error]
  );
  const { sideMenuOpen } = useSelector((state) => state.UI);
  const reset = useCallback(() => dispatch(resetChangeRequests()), [dispatch]);

  const { loadingChangeRequests, filteredChangeRequests } = useSelector(
    (state) => state.adminChangeRequests
  );

  const handleChangeSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleChangeRequest = (e) => {
    setRequestStatus(e.target.value);
  };
  const handleChangeLicense = (e) => {
    setLicenseStatus(e.target.value);
  };

  const handleChangeExpanded = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    loadRequests();
    return () => reset();
  }, [reset, loadRequests]);

  useEffect(() => {
    if (!loadingChangeRequests) {
      dispatch(filterChangeRequest(searchTerm, requestStatus, licenseStatus));
    }
  }, [
    searchTerm,
    requestStatus,
    licenseStatus,
    dispatch,
    loadingChangeRequests,
  ]);

  useEffect(() => {
    if (filteredChangeRequests && !expanded) {
      const obj = {};

      for (let r of filteredChangeRequests) {
        obj[r.id] = false;
      }

      setExpanded(obj);
    }
  }, [expanded, filteredChangeRequests]);

  return (
    <div>
      {snackbar}
      <Header headLine="Change Requests"></Header>
      <AppBar
        sx={{
          height: 80,
          marginTop: "108px",
          backgroundColor: "#FFFFFF",
          borderBottom: "solid",
          borderBottomColor: "#E7E9EF",
          borderBottomWidth: "thin",
          boxShadow: " 0px 4px 3px -3px rgba(0,0,0,0.6)",
        }}
        elevation={0}
      >
        <Grid
          sx={{
            marginLeft: sideMenuOpen ? "240px" : "64px",
            height: "60px",
            paddingInline: "10px",
            width: window.innerWidth - (sideMenuOpen ? 240 : 64),
          }}
          container
          spacing={2}
        >
          <Grid item xs={3}>
            <OutlinedInput
              id="search"
              placeholder="Search"
              sx={{
                mt: 2,
                width: "100%",
                height: "40px",
                "& .MuiInputBase-root": { height: "40px" },
                "& .MuiOutlinedInput": { height: "40px" },
              }}
              type="text"
              fullWidth
              value={searchTerm}
              onChange={handleChangeSearch}
              endAdornment={
                <SearchRoundedIcon
                  sx={{ color: "#8792AB", fontSize: "1rem" }}
                />
              }
            />
          </Grid>
          <Grid item xs={2}>
            <FormControl sx={{ mt: 2, minWidth: "100%" }} size="small">
              <InputLabel id="demo-select-small">Request Status</InputLabel>
              <Select
                labelId="Request-status"
                id="Request-status"
                value={requestStatus}
                label="Request Status"
                onChange={handleChangeRequest}
              >
                {requestOptions.map((option) => {
                  return (
                    <MenuItem value={option.value} key={option.id}>
                      {option.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={2}>
            <FormControl sx={{ mt: 2, minWidth: "100%" }} size="small">
              <InputLabel id="demo-select-small">License Status</InputLabel>
              <Select
                labelId="License-status"
                id="License-status"
                value={licenseStatus}
                label="License Status"
                onChange={handleChangeLicense}
              >
                {licenseOptions.map((option) => {
                  return (
                    <MenuItem value={option.value} key={option.id}>
                      {option.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </AppBar>
      {loadingChangeRequests || !expanded ? (
        <ScreenLoader />
      ) : (
        <Grid container>
          <Grid item xs={7}>
            <Grid
              container
              direction="column"
              sx={{
                p: "20px",
                marginTop: "75px",
              }}
            >
              {filteredChangeRequests?.map((changeRequest) => (
                <Grid
                  key={changeRequest.id}
                  item
                  container
                  direction="column"
                  sx={{
                    backgroundColor:
                      changeRequest.status === "RUNNING"
                        ? "#FFFFFF"
                        : "#F2F4F8",
                    p: 2,
                    my: 1,
                    border: "1px solid #E7E9EF",
                    borderRadius: "6px",
                  }}
                >
                  <Grid item container justifyContent="flex-end">
                    <Chip
                      label={
                        changeRequest.status === "EXPIRED"
                          ? "Expired"
                          : "Active"
                      }
                      sx={{
                        height: "20px",
                        backgroundColor:
                          changeRequest.status === "EXPIRED"
                            ? "#FFD8D6"
                            : "#E5F6E4",
                        color:
                          changeRequest.status === "EXPIRED"
                            ? "#400F0C"
                            : "#002B00",
                      }}
                    />
                  </Grid>
                  {/*Name */}
                  <Grid item container>
                    <Grid item xs={3}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          lineHeight: "21px",
                        }}
                      >
                        licensed to:
                      </Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          fontWeight: 550,
                          lineHeight: "21px",
                        }}
                      >
                        {changeRequest.bankName} {changeRequest.bankBic}
                      </Typography>
                    </Grid>
                  </Grid>
                  {/*Key */}
                  <Grid item container>
                    <Grid item xs={3}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          lineHeight: "21px",
                        }}
                      >
                        license Key:
                      </Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          fontWeight: 550,
                          lineHeight: "21px",
                        }}
                      >
                        {changeRequest.keySimplified}
                      </Typography>
                    </Grid>
                  </Grid>
                  {expanded[changeRequest.id] ? (
                    <>
                      {/*Users */}
                      <Grid item container>
                        <Grid item xs={3}>
                          <Typography
                            sx={{
                              color: "#050D2E",
                              fontSize: ".875rem",
                              lineHeight: "21px",
                            }}
                          >
                            Users:
                          </Typography>
                        </Grid>
                        <Grid item xs={9}>
                          <Typography
                            sx={{
                              color: "#050D2E",
                              fontSize: ".875rem",
                              fontWeight: 550,
                              lineHeight: "21px",
                            }}
                          >
                            {changeRequest.allowedUsers}
                          </Typography>
                        </Grid>
                      </Grid>
                      {/*Messages */}
                      <Grid item container>
                        <Grid item xs={3}>
                          <Typography
                            sx={{
                              color: "#050D2E",
                              fontSize: ".875rem",
                              lineHeight: "21px",
                            }}
                          >
                            Messages/Day:
                          </Typography>
                        </Grid>
                        <Grid item xs={9}>
                          <Typography
                            sx={{
                              color: "#050D2E",
                              fontSize: ".875rem",
                              fontWeight: 550,
                              lineHeight: "21px",
                            }}
                          >
                            {changeRequest.messages}
                          </Typography>
                        </Grid>
                      </Grid>
                      {/*Renewal */}
                      <Grid item container>
                        <Grid item xs={3}>
                          <Typography
                            sx={{
                              color: "#050D2E",
                              fontSize: ".875rem",
                              lineHeight: "21px",
                            }}
                          >
                            {changeRequest.status === "RUNNING"
                              ? "License Renewal:"
                              : "Expiration Date:"}
                          </Typography>
                        </Grid>
                        <Grid item xs={9}>
                          <Typography
                            sx={{
                              color: "#050D2E",
                              fontSize: ".875rem",
                              lineHeight: "21px",
                              fontWeight: 550,
                            }}
                          >
                            {`${new Date(
                              changeRequest.expiryDate
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })} 
                        `}
                            {changeRequest.status === "RUNNING"
                              ? `(${calculateLeftDays(
                                  new Date(changeRequest?.creationDate),
                                  new Date(changeRequest?.expiryDate)
                                )} Days)`
                              : null}
                          </Typography>
                        </Grid>
                      </Grid>
                    </>
                  ) : null}

                  <Grid item>
                    <Button
                      variant="text"
                      sx={{ textTransform: "none", p: 0 }}
                      endIcon={
                        expanded[changeRequest.id] ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )
                      }
                      onClick={() => handleChangeExpanded(changeRequest.id)}
                    >
                      {expanded[changeRequest.id] ? "Show less" : "Show more"}
                    </Button>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Grid item container justifyContent="space-between">
                    <Typography
                      sx={{
                        color: "#050D2E",
                        fontSize: ".875rem",
                        lineHeight: "21px",
                      }}
                    >
                      Requested Modules
                    </Typography>
                    <Chip
                      label={
                        changeRequest.requestStatus === "NEW"
                          ? "Pending"
                          : "Done"
                      }
                      sx={{
                        height: "20px",
                        backgroundColor:
                          changeRequest.requestStatus === "NEW"
                            ? "#FFF0E4"
                            : "#E5F1FF",
                        color:
                          changeRequest.requestStatus === "NEW"
                            ? "#002B00"
                            : "#002B00",
                      }}
                    />
                  </Grid>

                  <Grid item container sx={{ my: 1 }}>
                    {changeRequest.modules.map((module) => (
                      <Chip
                        key={module.id}
                        avatar={
                          <Avatar
                            alt="Natacha"
                            src={getModuleIcon(module.id)}
                          />
                        }
                        label={module.name}
                        sx={{
                          m: 1,
                          height: "25px",
                          backgroundColor: "#F2F4F8",
                          border: "1px solid #DBDEE6",
                          color: "#050D2E",
                        }}
                      />
                    ))}
                  </Grid>
                  <Grid item container spacing={3} sx={{ my: 1 }}>
                    <Grid item>
                      {changeRequest.requestStatus === "NEW" && (
                        <Button
                          disableElevation
                          onClick={() => {
                            dispatch(
                              acceptChangeRequest(
                                changeRequest.id,
                                error,
                                success
                              )
                            ).then(loadRequests());
                          }}
                          startIcon={<CheckIcon />}
                          variant="contained"
                          sx={{
                            textTransform: "none",
                            fontSize: ".875rem",
                            fontWeight: 500,
                            color: "",
                            letterSpacing: "-0.47px",
                            backgroundColor: "#1492AF",
                          }}
                        >
                          Mark As Done
                        </Button>
                      )}
                    </Grid>
                    <Grid item>
                      {changeRequest.requestStatus !== "ACCEPTED" && (
                        <Button
                          disableElevation
                          onClick={() => {
                            dispatch(
                              deleteChangeRequest(
                                changeRequest.id,
                                error,
                                success
                              )
                            );
                          }}
                          sx={{
                            textTransform: "none",
                            fontSize: ".875rem",
                            fontWeight: 500,
                            color: "#50566E",
                            letterSpacing: "-0.47px",
                          }}
                          variant="text"
                          startIcon={<DeleteIcon />}
                        >
                          Delete Request
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default ChangeRequests;
